import React, { useEffect, useState } from 'react';
import AsyncSelect from 'react-select/async';
import { Alert, Button, Input } from 'reactstrap';
import { FaTrash } from 'react-icons/fa';
import clientService from '../../services/clientService';
import productService from '../../services/productService';
import useAvailableStock from '../../hooks/useAvailableStock';
import { documentDiscount } from '../../helpers/purchaseDiscount';
import './ClientArticles.css';

const sameId = (a, b) => String(a) === String(b);
const money = value => Number(value || 0).toLocaleString('fr-MA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// The client form owns its catalogue; documents load it without modifying associations.
export default function ClientArticles({ articles, onChange, clientId, formik, quantityField = 'quantite', disabled = false }) {
    const managed = Boolean(onChange);
    const [catalogue, setCatalogue] = useState([]);
    const [loadedClient, setLoadedClient] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [retry, setRetry] = useState(0);
    const [search, setSearch] = useState('');
    useEffect(() => {
        if (managed) return undefined;
        let active = true;
        setCatalogue([]);
        setError('');
        setLoading(Boolean(clientId));
        setLoadedClient(null);
        if (clientId) clientService.articles(clientId).then(response => {
            if (active) { setCatalogue(response.data.body || []); setLoadedClient(clientId); }
        }).catch(() => { if (active) setError('Impossible de charger les articles du client.'); })
            .finally(() => { if (active) setLoading(false); });
        return () => { active = false; };
    }, [clientId, managed, retry]);

    const lines = formik?.values.lignes || [];
    const rows = managed ? articles : (sameId(loadedClient, clientId) ? catalogue : []);
    const allRows = (rows || []).map(row => managed ? row : { ...row, _line: lines.find(line => sameId(line.articleId, row.articleId)) });
    if (!managed) lines.forEach(line => {
        if (!allRows.some(row => row._line === line)) allRows.push({
            articleId: line.articleId, articleLibelle: line.articleNom, codeBarre: line.codeBarre,
            unite: line.unite, tauxTVA: line.tauxTVA, prixVenteTTC: line.prixUnitaireTTC,
            _line: line,
        });
    });
    const availability = useAvailableStock(managed ? [] : allRows.map(row => row.articleId));
    const locked = disabled || (!managed && !clientId);
    const commit = next => {
        const rounded = value => Math.round((value + Number.EPSILON) * 100) / 100;
        const calculated = next.map(line => {
            const price = Number(line.prixUnitaireTTC || 0);
            const rate = Number(line.tauxTVA || 0);
            const montantTTC = rounded(Number(line[quantityField] || 0) * price);
            const montantHT = rounded(Number(line[quantityField] || 0) * price / (1 + rate));
            return { ...line, prixUnitaireHT: rounded(price / (1 + rate)), montantTTC, montantHT, montantTaxe: rounded(montantTTC - montantHT) };
        });
        formik.setValues(previous => {
            const gross = rounded(calculated.reduce((sum, line) => sum + line.montantTTC, 0));
            const discount = documentDiscount(previous, gross);
            return { ...previous, lignes: calculated,
            montantTotalHT: rounded(calculated.reduce((sum, line) => sum + line.montantHT, 0)),
            montantTotalAvantRemise: gross,
            montantRemise: discount,
            montantTotalTTC: rounded(gross - discount),
            montantTotalTaxe: rounded(calculated.reduce((sum, line) => sum + line.montantTaxe, 0)),
        }; });
    };
    const pick = row => {
        if (lines.some(line => sameId(line.articleId, row.articleId))) return;
        commit([...lines, { articleId: row.articleId, articleNom: row.articleLibelle, codeBarre: row.codeBarre,
            unite: row.unite, tauxTVA: Number(row.tauxTVA ?? 0.2), prixUnitaireTTC: Number(row.prixVenteTTC || 0), [quantityField]: 1 }]);
    };
    const changeLine = (target, field, value) => commit(lines.map(line => line === target ? { ...line, [field]: value } : line));
    const remove = (id, target) => managed ? onChange(articles.filter(row => !sameId(row.articleId, id))) : commit(lines.filter(line => line !== target));
    const filtered = allRows.filter(row => `${row.articleLibelle || ''} ${row.codeBarre || ''}`.toLowerCase().includes(search.toLowerCase()));

    return <section className="client-articles">
        <div className="client-articles-heading"><div><h5>Articles du client</h5>
            <small className="text-muted">{managed ? 'Associez les articles et leur prix de vente.' : 'Sélectionnez les articles à ajouter, puis indiquez les quantités.'}</small></div>
            <span className="badge bg-primary">{allRows.length} article(s)</span></div>
        {error && <Alert color="danger">{error} {!managed && <Button type="button" size="sm" onClick={() => setRetry(value => value + 1)}>Réessayer</Button>}</Alert>}
        {!managed && !clientId && <Alert color="info">Sélectionnez un client pour afficher ses articles.</Alert>}
        {loading && <p role="status">Chargement des articles…</p>}
        {allRows.length > 0 && <Input className="mb-3" aria-label="Rechercher dans les articles du client" placeholder="Nom ou code-barres…" value={search} onChange={event => setSearch(event.target.value)} />}
        <div className="client-articles-list">
            {filtered.map(row => {
                const line = row._line;
                const name = row.articleLibelle || line?.articleNom || 'Article';
                return <div key={`${row.articleId}-${line ? lines.indexOf(line) : 'catalogue'}`} className={`client-article-row ${line ? 'is-selected' : ''}`}>
                    <div className="client-article-name">
                        {!managed && <Input type="checkbox" aria-label={`Sélectionner ${name}`} checked={Boolean(line)} disabled={locked} onChange={() => line ? remove(row.articleId, line) : pick(row)} />}
                        <div><strong>{name}</strong><small>{row.codeBarre || '—'} · {row.unite || 'Unité'} · TVA {Number(row.tauxTVA ?? 0.2) * 100} %</small>
                            {!managed && <small>Stock disponible : {availability.quantities[row.articleId] ?? (availability.error ? 'indisponible' : '…')}</small>}</div>
                    </div>
                    {!managed && line && <label>Quantité<Input type="number" min="0.001" step="any" required value={line[quantityField]} disabled={locked} onChange={event => changeLine(line, quantityField, event.target.value === '' ? '' : Number(event.target.value))} /></label>}
                    {(managed || line) ? <label>Prix TTC (DH)<Input type="number" min="0" step="0.01" required disabled={locked} value={managed ? row.prixVenteTTC : line.prixUnitaireTTC}
                        onChange={event => {
                            const value = event.target.value === '' ? '' : Number(event.target.value);
                            if (managed) onChange(articles.map(item => sameId(item.articleId, row.articleId) ? { ...item, prixVenteTTC: value } : item));
                            else changeLine(line, 'prixUnitaireTTC', value);
                        }} /></label> : <span>{money(row.prixVenteTTC)} DH</span>}
                    {!managed && line && <strong>{money(line.montantTTC)} DH</strong>}
                    {(managed || line) && <Button type="button" color="danger" outline size="sm" title="Retirer cet article" aria-label={`Retirer ${name}`} disabled={locked} onClick={() => remove(row.articleId, line)}><FaTrash /></Button>}
                </div>;
            })}
        </div>
        {!loading && !filtered.length && <p className="text-muted my-3">{search ? 'Aucun article trouvé.' : 'Aucun article associé. Ajoutez un article ci-dessous.'}</p>}
        {!disabled && <div className="mt-3"><label className="mb-2">{managed ? 'Associer un article' : 'Ajouter un autre article'}</label>
            <AsyncSelect key={clientId || 'new'} inputId="client-article-search" aria-label="Rechercher un article à ajouter" value={null} isDisabled={locked || loading || (!managed && !sameId(loadedClient, clientId))} defaultOptions
                placeholder="Rechercher par nom ou code-barres…" loadOptions={async query => {
                    try {
                        const result = await productService.findAll(query, 0, 30);
                        return (result.body?.content || []).map(product => ({ value: product.id, label: `${product.libelle} · ${product.codeBarre || ''}`, product }));
                    } catch { setError('Impossible de rechercher les articles.'); return []; }
                }} isOptionDisabled={option => (managed ? allRows : lines).some(row => sameId(row.articleId, option.value))}
                onChange={option => {
                    if (!option) return;
                    const product = option.product;
                    const row = allRows.find(item => sameId(item.articleId, product.id)) || { articleId: product.id, articleLibelle: product.libelle, codeBarre: product.codeBarre,
                        unite: product.unite, tauxTVA: product.tauxTVAVente ?? 0.2, prixVenteTTC: product.prixVenteTTC1 ?? 0 };
                    if (managed) { if (!articles.some(item => sameId(item.articleId, row.articleId))) onChange([...articles, row]); }
                    else pick(row);
                }} />
        </div>}
        {!managed && formik.touched.lignes && typeof formik.errors.lignes === 'string' && <div className="text-danger mt-2">{formik.errors.lignes}</div>}
    </section>;
}
